import Link from 'next/link'
import styled from 'styled-components'
import { Button } from 'ui-kit/button'

// Hero Section
export const Hero = styled.section`
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.palette.primary.main} 0%,
    ${({ theme }) => theme.palette.primary.light} 100%
  );
  color: ${({ theme }) => theme.palette.primary.contrastText};
  padding: 80px 20px;
  text-align: center;
  margin-bottom: 60px;

  ${({ theme }) => theme.breakpoints.down.md} {
    padding: 60px 20px;
    margin-bottom: 40px;
  }
`

export const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`

export const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 24px;

  ${({ theme }) => theme.breakpoints.down.md} {
    font-size: 2.5rem;
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    font-size: 2rem;
  }
`

export const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  margin-bottom: 32px;
  opacity: 0.9;

  ${({ theme }) => theme.breakpoints.down.md} {
    font-size: 1.25rem;
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    font-size: 1rem;
  }
`

export const HeroButton = styled(Link)`
  display: inline-block;
  background-color: #fff;
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 1.125rem;
  font-weight: 600;
  padding: 16px 32px;
  border-radius: 4px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    font-size: 1rem;
    padding: 12px 24px;
  }
`

// Feature Section
export const FeatureSection = styled.section`
  max-width: 1200px;
  margin: 0 auto 80px;
  padding: 0 20px;
  text-align: center;

  h2 {
    font-size: 2.5rem;
    margin-bottom: 48px;
    color: ${({ theme }) => theme.palette.text.primary};

    ${({ theme }) => theme.breakpoints.down.md} {
      font-size: 2rem;
      margin-bottom: 32px;
    }
  }

  ${({ theme }) => theme.breakpoints.down.md} {
    margin-bottom: 60px;
  }
`

export const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 40px;

  ${({ theme }) => theme.breakpoints.down.sm} {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`

export const FeatureCard = styled.div`
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 8px;
  padding: 30px 20px;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  }
`

export const FeatureIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`

export const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.palette.text.primary};
`

export const FeatureDescription = styled.p`
  color: ${({ theme }) => theme.palette.text.primary};
  font-size: 1rem;
  line-height: 1.6;
`

// CTA Section
export const CTASection = styled.section`
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.palette.primary.main} 0%,
    ${({ theme }) => theme.palette.primary.light} 100%
  );
  color: ${({ theme }) => theme.palette.primary.contrastText};
  padding: 80px 20px;
  margin-bottom: 60px;

  ${({ theme }) => theme.breakpoints.down.md} {
    padding: 60px 20px;
    margin-bottom: 40px;
  }
`

export const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
`

export const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 20px;

  ${({ theme }) => theme.breakpoints.down.md} {
    font-size: 2rem;
  }
`

export const CTADescription = styled.p`
  font-size: 1.25rem;
  margin-bottom: 32px;
  opacity: 0.9;

  ${({ theme }) => theme.breakpoints.down.md} {
    font-size: 1.125rem;
  }
`

export const CTAButton = styled(Link)`
  display: inline-block;
  background-color: #fff;
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 1.125rem;
  font-weight: 600;
  padding: 16px 32px;
  border-radius: 4px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    font-size: 1rem;
    padding: 12px 24px;
  }
`

// Links Section
export const LinksSection = styled.section`
  max-width: 1200px;
  margin: 0 auto 80px;
  padding: 0 20px;

  ${({ theme }) => theme.breakpoints.down.md} {
    margin-bottom: 60px;
  }
`

export const LinksTitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 30px;
  text-align: center;
  color: ${({ theme }) => theme.palette.text.primary};

  ${({ theme }) => theme.breakpoints.down.md} {
    font-size: 1.75rem;
    margin-bottom: 24px;
  }
`

export const LinksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 40px;

  ${({ theme }) => theme.breakpoints.down.sm} {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`

export const LinkCategory = styled.div`
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
`

export const CategoryTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 16px;
  color: ${({ theme }) => theme.palette.text.primary};
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`

export const LinksList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`

export const LinkItem = styled.li`
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`

export const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.palette.primary.main};
  font-size: 1.125rem;
  text-decoration: none;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.light};
    transform: translateX(5px);
  }

  &:before {
    content: '→';
    margin-right: 8px;
    transition: transform 0.2s ease;
  }

  &:hover:before {
    transform: translateX(3px);
  }
`

// Content Section
export const ContentSection = styled.section`
  max-width: 1200px;
  margin: 0 auto 80px;
  padding: 0 20px;

  ${({ theme }) => theme.breakpoints.down.md} {
    margin-bottom: 60px;
  }
`

export const ContentContainer = styled.div`
  font-size: 1.125rem;
  line-height: 1.8;
  color: ${({ theme }) => theme.palette.text.primary};
`

export const ContentCard = styled.div`
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 12px;
  padding: 30px;
  margin-bottom: 40px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.07);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  overflow: hidden;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 20px rgba(0, 0, 0, 0.1);
  }

  ${({ theme }) => theme.breakpoints.down.md} {
    padding: 25px;
    margin-bottom: 30px;
  }
`

export const ContentCardHeader = styled.div`
  margin-bottom: 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  padding-bottom: 15px;
`

export const ContentIntro = styled.div`
  margin-bottom: 40px;

  ${({ theme }) => theme.breakpoints.down.md} {
    margin-bottom: 30px;
  }
`

export const ContentTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  margin: 0 0 16px;
  color: ${({ theme }) => theme.palette.text.primary};

  ${({ theme }) => theme.breakpoints.down.md} {
    font-size: 1.875rem;
    margin: 0 0 12px;
  }
`

export const ContentSubheading = styled.h3`
  font-size: 1.625rem;
  font-weight: 600;
  margin: 0 0 16px;
  color: ${({ theme }) => theme.palette.text.primary};

  ${({ theme }) => theme.breakpoints.down.md} {
    font-size: 1.375rem;
  }
`

export const ContentParagraph = styled.p`
  margin: 0 0 16px;

  &:last-child {
    margin-bottom: 0;
  }

  ${({ theme }) => theme.breakpoints.down.md} {
    margin: 0 0 14px;
  }
`

export const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 30px;
  margin: 40px 0;

  ${({ theme }) => theme.breakpoints.down.lg} {
    grid-template-columns: 1fr;
  }
`

export const ContentCalloutCard = styled.div`
  background: linear-gradient(
    135deg,
    ${({ theme }) => `${theme.palette.primary.main}22`} 0%,
    ${({ theme }) => `${theme.palette.primary.light}22`} 100%
  );
  border-left: 4px solid ${({ theme }) => theme.palette.primary.main};
  padding: 20px 25px;
  margin: 25px 0;
  border-radius: 0 8px 8px 0;
`

export const InlineLink = styled(Link)`
  color: ${({ theme }) => theme.palette.text.primary};
  text-decoration: none;
  font-weight: 500;
  margin: 0px 5px;
  transition: color 0.2s ease;

  &:hover {
    text-decoration: underline;
  }
`

export const ActionButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.primary.contrastText};
  padding: 12px 20px;
  border-radius: 6px;
  text-decoration: none;
  font-weight: 600;
  margin: 8px 0;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.light};
    transform: translateY(-2px);
  }

  &:after {
    content: '→';
    margin-left: 8px;
    transition: transform 0.2s ease;
  }

  &:hover:after {
    transform: translateX(3px);
  }
`

export const SummaryCard = styled.div`
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.palette.primary.main} 0%,
    ${({ theme }) => theme.palette.primary.light} 100%
  );
  color: ${({ theme }) => theme.palette.primary.contrastText};
  padding: 30px;
  border-radius: 12px;
  margin-top: 40px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);

  ${InlineLink} {
    color: white;
    text-decoration: underline;
    text-decoration-color: rgba(255, 255, 255, 0.5);

    &:hover {
      text-decoration-color: white;
    }
  }
`

// Feature Pros Cards
export const ProsSection = styled.section`
  max-width: 1200px;
  margin: 0 auto 80px;
  padding: 0 20px;

  ${({ theme }) => theme.breakpoints.down.md} {
    margin-bottom: 60px;
  }
`

export const ProsTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 50px;
  color: ${({ theme }) => theme.palette.text.primary};

  ${({ theme }) => theme.breakpoints.down.md} {
    font-size: 2rem;
    margin-bottom: 40px;
  }
`

export const ProsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;

  ${({ theme }) => theme.breakpoints.down.md} {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 20px;
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  ${({ theme }) => theme.breakpoints.down.xs} {
    grid-template-columns: 1fr;
  }
`

export const ProCard = styled.div`
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 12px;
  padding: 25px 20px;
  height: 100%;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-7px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(
      90deg,
      ${({ theme }) => theme.palette.primary.main} 0%,
      ${({ theme }) => theme.palette.primary.light} 100%
    );
  }
`

export const ProIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 15px;
  color: ${({ theme }) => theme.palette.primary.main};
`

export const ProTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0 0 12px;
  color: ${({ theme }) => theme.palette.text.primary};
`

export const ProDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.palette.text.primary};
  margin: 0 0 15px;
  flex-grow: 1;
`

export const ProActionButton = styled.div`
  display: inline-flex;
  align-items: center;
  font-size: 0.95rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.primary};
  text-decoration: none;
  margin-top: auto;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.palette.primary.light};
  }

  &:after {
    content: '→';
    transition: transform 0.2s ease;
  }

  &:hover:after {
    transform: translateX(4px);
  }
`

// Typing Mentor Coach Featured Section
export const CoachSection = styled.section`
  max-width: 1200px;
  margin: 0 auto 80px;
  padding: 0 20px;

  ${({ theme }) => theme.breakpoints.down.md} {
    margin-bottom: 60px;
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    margin-bottom: 40px;
    padding: 0 15px;
  }
`

export const CoachContainer = styled.div`
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.palette.primary.main} 0%,
    ${({ theme }) => theme.palette.primary.light} 100%
  );
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  position: relative;

  ${({ theme }) => theme.breakpoints.down.md} {
    flex-direction: column;
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    border-radius: 8px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  }
`

export const CoachContent = styled.div`
  flex: 1;
  padding: 40px;
  color: ${({ theme }) => theme.palette.primary.contrastText};

  ${({ theme }) => theme.breakpoints.down.md} {
    padding: 30px;
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    padding: 20px 15px;
  }
`

export const CoachTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0 0 20px;
  color: ${({ theme }) => theme.palette.primary.contrastText};

  ${({ theme }) => theme.breakpoints.down.md} {
    font-size: 2rem;
    margin: 0 0 16px;
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    font-size: 1.75rem;
    margin: 0 0 12px;
  }
`

export const CoachSubtitle = styled.p`
  font-size: 1.25rem;
  margin-bottom: 25px;
  opacity: 0.9;

  ${({ theme }) => theme.breakpoints.down.md} {
    font-size: 1.125rem;
    margin-bottom: 20px;
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    font-size: 1rem;
    margin-bottom: 16px;
    line-height: 1.5;
  }
`

export const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 30px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;

  ${({ theme }) => theme.breakpoints.down.md} {
    gap: 16px;
    margin: 0 0 25px;
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    grid-template-columns: 1fr;
    gap: 8px;
    margin: 0 0 15px;
  }
`

export const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;
  margin-bottom: 15px;

  &:before {
    content: '✓';
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    background-color: ${({ theme }) => `${theme.palette.primary.contrastText}25`};
    border-radius: 50%;
    margin-right: 12px;
    color: ${({ theme }) => theme.palette.primary.contrastText};
    font-weight: bold;
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    margin-bottom: 8px;

    &:before {
      min-width: 18px;
      height: 18px;
      font-size: 0.8rem;
      margin-right: 8px;
    }
  }
`

export const FeatureText = styled.span`
  font-size: 1.05rem;

  ${({ theme }) => theme.breakpoints.down.sm} {
    font-size: 0.95rem;
  }
`

export const CoachCTA = styled(Link)`
  display: inline-block;
  background-color: ${({ theme }) => theme.palette.background.paper};
  color: ${({ theme }) => theme.palette.primary};
  font-size: 1.125rem;
  font-weight: 600;
  padding: 16px 28px;
  border-radius: 4px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 10px;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }

  ${({ theme }) => theme.breakpoints.down.md} {
    font-size: 1.0625rem;
    padding: 14px 24px;
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    font-size: 0.9375rem;
    padding: 10px 16px;
    width: 100%;
    text-align: center;
    margin-top: 0;
  }
`

export const ImageContainer = styled.div`
  flex: 0 0 40%;
  position: relative;
  background-size: cover;

  ${({ theme }) => theme.breakpoints.down.md} {
    min-height: 300px;
    flex: none;
    order: -1;
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    min-height: 120px;
    background-position: center center;
  }
`

export const AILabel = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: ${({ theme }) => `${theme.palette.background.paper}33`};
  backdrop-filter: blur(5px);
  padding: 8px 15px;
  border-radius: 20px;
  color: ${({ theme }) => theme.palette.primary.contrastText};
  font-weight: 600;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  z-index: 2;

  &:before {
    content: '🤖';
    margin-right: 6px;
  }

  ${({ theme }) => theme.breakpoints.down.sm} {
    top: 10px;
    right: 10px;
    padding: 5px 10px;
    font-size: 0.75rem;
    border-radius: 14px;
  }
`
